import { create } from "zustand";
import {
    cleanAccessTokenFromStorage,
    cleanRefreshTokenFromStorage,
    getAccessTokenFromStorage,
    setAccessTokenToStorage,
    setRefreshTokenToStorage
} from "../auth/tokenStorage";
import { getUserIdentity } from "../auth/tokenFunctions";
import { identifyUser, resetUser } from "../telemetry";

interface AuthState {
  isLoggedIn: boolean;
  login: (token: string, refreshToken: string) => void;
  logout: () => void;
}

// Attaches a stable, non-PII user identity (see getUserIdentity) to error
// telemetry so PostHog exceptions can be traced to a user/account. Never
// throws — telemetry failures must not affect auth.
function identifyFromToken(token: string): void {
  const identity = getUserIdentity(token);
  if (!identity) {
    return;
  }

  // getUserIdentity sets isPii when the only identifier it could find was the email in `sub`.
  // Sending that as a PostHog distinct_id would put a patient-facing email address in telemetry,
  // so stay anonymous instead — an unidentified error is better than a PII leak.
  if (identity.isPii) {
    return;
  }

  identifyUser(
    identity.userId,
    identity.accountId ? { accountId: identity.accountId } : undefined
  );
}

const initialToken = getAccessTokenFromStorage();

// Session restore on app start (e.g. page reload with a stored token) — otherwise
// reloaded sessions would report anonymous errors. Deliberately NOT called at module
// scope: this module is part of an import cycle with auth/tokenFunctions, and running
// it during module evaluation can hit a temporal dead zone there. main.tsx calls it
// once the module graph is fully initialized.
export function identifyStoredSession(): void {
  const token = getAccessTokenFromStorage();
  if (token) {
    identifyFromToken(token);
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  isLoggedIn: !!initialToken, // Check initial state from localStorage
  login: (token, refreshToken) => {
    setAccessTokenToStorage(token);
    setRefreshTokenToStorage(refreshToken);
    identifyFromToken(token);
    set({ isLoggedIn: true });
  },
  logout: () => {
    cleanAccessTokenFromStorage();
    cleanRefreshTokenFromStorage();
    resetUser();
    set({ isLoggedIn: false });
  },
}));

export const useStoreLogin = () => useAuthStore((state) => state.login);
export const useStoreLogout = () => useAuthStore((state) => state.logout);
export const useStoreIsLoggedIn = () =>
  useAuthStore((state) => state.isLoggedIn);
